import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Plus, Trash2, Upload, Search, FileText } from "lucide-react";
import { format } from "date-fns";
import { useSortableTable } from "@/hooks/useSortableTable";
import { safeFormat } from "@/lib/dateUtils";
import { EditableCell } from "@/components/EditableCell";
import InvoiceUpload from "@/components/InvoiceUpload";
import { PageControls } from "@/components/PageControls";

const CATEGORIES = ['General', 'Products', 'Rent', 'Utilities', 'Marketing', 'Equipment', 'Insurance', 'Training', 'Other'];
const CAT_OPTIONS = CATEGORIES.map(c => ({ value: c, label: c, className: 'bg-muted text-foreground font-medium' }));

export default function Expenses() {
  const [expenses, setExpenses] = useState([]);
  const [total, setTotal] = useState(0);
  const [dialog, setDialog] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [form, setForm] = useState({ date: format(new Date(), 'yyyy-MM-dd'), category: 'General', net: 0, tax: 0, gross: 0, supplier_name: '', notes: '' });

  const load = async () => {
    try {
      const params = new URLSearchParams({ limit: String(pageSize), skip: String((page - 1) * pageSize) });
      if (search.trim()) params.append('search', search.trim());
      if (filterCat !== 'all') params.append('category', filterCat);
      const res = await axios.get(`${API}/expenses?${params}`);
      setExpenses(res.data.expenses || []);
      setTotal(res.data.total || 0);
    } catch (e) {  }
  };

  useEffect(() => { setPage(1); }, [search, filterCat]);
  useEffect(() => { load(); }, [search, filterCat, page, pageSize]);

  const { sortedData, SortHeader } = useSortableTable(expenses, 'date', 'desc');
  const filteredTotal = sortedData.reduce((s, e) => s + (e.gross || 0), 0);

  const saveInline = async (expId, field, value) => {
    try {
      const numFields = ['net', 'tax', 'gross'];
      const val = numFields.includes(field) ? parseFloat(value) || 0 : value;
      await axios.put(`${API}/expenses/${expId}`, { [field]: val });
      setExpenses(prev => prev.map(e => e.id === expId ? { ...e, [field]: val } : e));
      toast.success('Updated');
    } catch (e) { toast.error('Failed to update'); }
  };

  const save = async () => {
    try {
      await axios.post(`${API}/expenses`, form);
      toast.success('Expense added');
      setDialog(false);
      load();
    } catch (e) { toast.error('Failed to save'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    try { await axios.delete(`${API}/expenses/${id}`); toast.success('Deleted'); load(); } catch (e) { toast.error('Failed'); }
  };

  return (
    <div className="space-y-4 fade-in">
      <Toaster position="top-center" />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Playfair Display' }}>Expenses</h1>
          <p className="text-sm text-muted-foreground">Total: £{filteredTotal.toFixed(2)} · {sortedData.length} records · Click cells to edit</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setInvoiceOpen(true)} className="rounded-xl">
            <Upload size={16} className="mr-1" /> Upload Invoice
          </Button>
          <Button onClick={() => { setForm({ date: format(new Date(), 'yyyy-MM-dd'), category: 'General', net: 0, tax: 0, gross: 0, supplier_name: '', notes: '' }); setDialog(true); }} className="rounded-xl">
            <Plus size={16} className="mr-1" /> Add Expense
          </Button>
        </div>
      </div>

      {/* Search + Filter */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search supplier, notes, invoice #" className="pl-9 rounded-xl" />
        </div>
        <Select value={filterCat} onValueChange={setFilterCat}>
          <SelectTrigger className="w-36 rounded-xl"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="rounded-2xl border shadow-[0_10px_30px_rgba(43,18,27,0.06)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <SortHeader field="date">Date</SortHeader>
                <SortHeader field="category">Category</SortHeader>
                <SortHeader field="supplier_name">Supplier</SortHeader>
                <SortHeader field="net">Net</SortHeader>
                <SortHeader field="tax">Tax</SortHeader>
                <SortHeader field="gross">Gross</SortHeader>
                <th className="text-left p-3 font-medium text-muted-foreground">Notes</th>
                <th className="text-left p-3 font-medium text-muted-foreground w-12"></th>
              </tr>
            </thead>
            <tbody>
              {sortedData.map(exp => (
                <tr key={exp.id} className="border-b border-border/50 hover:bg-muted/10 transition-colors">
                  <td className="p-1.5 pl-3 text-xs whitespace-nowrap">{exp.date ? safeFormat(exp.date, 'd MMM yyyy') : '-'}</td>
                  <td className="p-1.5">
                    <EditableCell value={exp.category} field="category" options={CAT_OPTIONS}
                      onSave={(f, v) => saveInline(exp.id, f, v)} />
                  </td>
                  <td className="p-1.5">
                    <EditableCell value={exp.supplier_name} field="supplier_name"
                      onSave={(f, v) => saveInline(exp.id, f, v)} className="text-xs" placeholder="supplier" />
                  </td>
                  <td className="p-1.5">
                    <EditableCell value={exp.net} field="net" type="number"
                      onSave={(f, v) => saveInline(exp.id, f, v)}
                      displayRender={v => <span className="tabular-nums text-xs">£{(parseFloat(v) || 0).toFixed(2)}</span>} />
                  </td>
                  <td className="p-1.5">
                    <EditableCell value={exp.tax} field="tax" type="number"
                      onSave={(f, v) => saveInline(exp.id, f, v)}
                      displayRender={v => <span className="tabular-nums text-xs">£{(parseFloat(v) || 0).toFixed(2)}</span>} />
                  </td>
                  <td className="p-1.5">
                    <EditableCell value={exp.gross} field="gross" type="number"
                      onSave={(f, v) => saveInline(exp.id, f, v)}
                      displayRender={v => <span className="tabular-nums text-xs font-medium">£{(parseFloat(v) || 0).toFixed(2)}</span>} />
                  </td>
                  <td className="p-1.5 max-w-[160px]">
                    <EditableCell value={exp.notes} field="notes"
                      onSave={(f, v) => saveInline(exp.id, f, v)} className="text-xs" placeholder="note" />
                  </td>
                  <td className="p-1.5">
                    <div className="flex gap-0.5">
                      {exp.invoice_file_id && <FileText size={11} className="text-blue-400" title="Has invoice" />}
                      <Button variant="ghost" size="icon" onClick={() => remove(exp.id)} className="h-6 w-6 rounded-lg text-destructive"><Trash2 size={11} /></Button>
                    </div>
                  </td>
                </tr>
              ))}
              {sortedData.length === 0 && <tr><td colSpan="8" className="p-8 text-center text-muted-foreground">No expenses found</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
      <PageControls page={page} pageSize={pageSize} total={total} onPage={setPage} onPageSize={setPageSize} />

      {/* Add Expense Dialog */}
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Playfair Display' }}>Add Expense</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Date</Label><Input type="date" value={form.date} onChange={e => setForm(p => ({...p, date: e.target.value}))} className="mt-1 rounded-xl" /></div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={v => setForm(p => ({...p, category: v}))}>
                <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Net (£)</Label><Input type="number" step="0.01" value={form.net} onChange={e => { const v = parseFloat(e.target.value) || 0; setForm(p => ({...p, net: v, gross: v + p.tax})); }} className="mt-1 rounded-xl" /></div>
            <div><Label>Tax (£)</Label><Input type="number" step="0.01" value={form.tax} onChange={e => { const v = parseFloat(e.target.value) || 0; setForm(p => ({...p, tax: v, gross: p.net + v})); }} className="mt-1 rounded-xl" /></div>
            <div><Label>Gross (£)</Label><Input type="number" step="0.01" value={form.gross} readOnly className="mt-1 rounded-xl bg-muted" /></div>
            <div><Label>Supplier</Label><Input value={form.supplier_name} onChange={e => setForm(p => ({...p, supplier_name: e.target.value}))} className="mt-1 rounded-xl" /></div>
            <div className="col-span-2"><Label>Notes</Label><Input value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} className="mt-1 rounded-xl" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={save} className="rounded-xl">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Upload */}
      <InvoiceUpload open={invoiceOpen} onClose={() => setInvoiceOpen(false)} onPosted={load} defaultPostTo="expense" />
    </div>
  );
}
